import { useQuery } from "@tanstack/react-query"
import { Eye } from "lucide-react"
import { useRef, useState } from "react"

import { api } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { FichaOrden } from "@/componentes/ordenes/FichaOrden"
import { CLASE } from "@/componentes/resumen/Listas"
import type { Partida } from "@/componentes/resumen/tipos"
import { monto } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import { Cajon } from "@/componentes/ui/Cajon"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { type ArchivoParaVer, VistaPreviaArchivo } from "@/componentes/ui/VistaPreviaArchivo"
import { comoFecha } from "@/lib/fechas"

type Cxc = {
  monto: string
  monto_cobrado: string
  saldo: string
  moneda: string
  sistema2_clave: string | null
}

type Rubro = {
  id: number
  concepto: string
  rubro_tipo: string | null
  monto: string
  cobrado: string
  moneda: string
  estado: string
  fecha_vencimiento: string | null
}

type Cxp = {
  monto: string
  monto_pagado: string
  saldo: string
  moneda: string
  sistema2_total: string | null
  sistema2_pagado: string | null
  orden_en_curso: { id: number; codigo: string; estado: string } | null
  documentos: { id: number; nombre: string; nombre_original: string }[]
}

function Dato({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs" style={{ color: "var(--fg-muted)" }}>
        {rotulo}
      </dt>
      <dd className="font-semibold tabular-nums">{valor}</dd>
    </div>
  )
}

function DetalleCxc({ p }: { p: Partida }) {
  const tabla = useRef<HTMLDivElement>(null)
  const cxc = useQuery({
    queryKey: ["cxc", p.ref_id],
    queryFn: () => api.get<Cxc>(`/api/v1/cxc/${p.ref_id}`),
  })
  const espejo = Boolean(cxc.data?.sistema2_clave)
  const rubros = useQuery({
    queryKey: ["cxc", p.ref_id, "sistema2"],
    queryFn: () => api.get<{ rubros: Rubro[] }>(`/api/v1/cxc/${p.ref_id}/sistema2`),
    enabled: espejo,
    retry: false,
  })
  if (cxc.isPending) return <EstadoCarga etiqueta="Consultando la cuenta…" />
  if (cxc.isError) {
    return (
      <EstadoError
        titulo="No se pudo consultar la cuenta"
        error={cxc.error}
        onReintentar={() => cxc.refetch()}
      />
    )
  }
  const c = cxc.data
  return (
    <div className="space-y-4">
      <dl className="tarjeta grid grid-cols-2 gap-3 p-4 sm:grid-cols-3">
        <Dato rotulo="Total" valor={`${monto(c.monto)} ${c.moneda}`} />
        <Dato rotulo="Cobrado" valor={`${monto(c.monto_cobrado)} ${c.moneda}`} />
        <Dato rotulo="Por cobrar" valor={`${monto(c.saldo)} ${c.moneda}`} />
      </dl>
      {espejo && rubros.isPending && <EstadoCarga etiqueta="Trayendo el detalle del 2.0…" />}
      {rubros.isError && (
        <EstadoError
          titulo="No se pudo leer el detalle del 2.0"
          error={rubros.error}
          onReintentar={() => rubros.refetch()}
        />
      )}
      {rubros.data && (
        <div ref={tabla} className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold">Servicios y gastos en el 2.0</h3>
            <Exportar
              titulo={`CxC de ${p.titulo}`}
              subtitulo={p.expediente ? `Expediente ${p.expediente}` : undefined}
              contenido={tabla}
            />
          </div>
          <Tabla etiqueta="Servicios y gastos de la cuenta">
            <Cabecera>
              <Th>Concepto</Th>
              <Th>Vence</Th>
              <Th>Estado</Th>
              <Th derecha>Monto</Th>
              <Th derecha>Cobrado</Th>
            </Cabecera>
            <Cuerpo>
              {rubros.data.rubros.length === 0 && (
                <TablaVacia colSpan={5}>El 2.0 no trae servicios para esta cuenta.</TablaVacia>
              )}
              {rubros.data.rubros.map((x) => (
                <Fila key={x.id} atenuada={x.estado === "ANULADO"}>
                  <Td>{x.concepto}</Td>
                  <Td numero>{x.fecha_vencimiento ? comoFecha(x.fecha_vencimiento) : "—"}</Td>
                  <Td>{x.estado}</Td>
                  <Td derecha numero>
                    {monto(x.monto)} {x.moneda}
                  </Td>
                  <Td derecha numero>
                    {monto(x.cobrado)} {x.moneda}
                  </Td>
                </Fila>
              ))}
            </Cuerpo>
          </Tabla>
        </div>
      )}
    </div>
  )
}

function DetalleCxp({ p, onOrden }: { p: Partida; onOrden: (id: number) => void }) {
  const [viendo, setViendo] = useState<ArchivoParaVer | null>(null)
  const cxp = useQuery({
    queryKey: ["cxp", p.ref_id],
    queryFn: () => api.get<Cxp>(`/api/v1/cxp/${p.ref_id}`),
  })
  if (cxp.isPending) return <EstadoCarga etiqueta="Consultando la cuenta…" />
  if (cxp.isError) {
    return (
      <EstadoError
        titulo="No se pudo consultar la cuenta"
        error={cxp.error}
        onReintentar={() => cxp.refetch()}
      />
    )
  }
  const c = cxp.data
  return (
    <div className="space-y-4">
      <dl className="tarjeta grid grid-cols-2 gap-3 p-4 sm:grid-cols-3">
        <Dato rotulo="Total" valor={`${monto(c.monto)} ${c.moneda}`} />
        <Dato rotulo="Pagado" valor={`${monto(c.monto_pagado)} ${c.moneda}`} />
        <Dato rotulo="Por pagar" valor={`${monto(c.saldo)} ${c.moneda}`} />
      </dl>
      {c.orden_en_curso && (
        <p className="flex flex-wrap items-center justify-between gap-2 text-sm">
          Se está pagando con la orden {c.orden_en_curso.codigo}.
          <Boton tamano="sm" onClick={() => onOrden((c.orden_en_curso as { id: number }).id)}>
            Ver la orden
          </Boton>
        </p>
      )}
      <section className="space-y-2" aria-label="Soportes">
        <h3 className="text-sm font-semibold">Soportes</h3>
        {c.documentos.length === 0 && (
          <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
            Esta cuenta no tiene soportes cargados.
          </p>
        )}
        {c.documentos.map((d) => (
          <p key={d.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
            {d.nombre || d.nombre_original}
            <Boton
              tamano="sm"
              variante="fantasma"
              aria-label={`Ver ${d.nombre || d.nombre_original}`}
              onClick={() =>
                setViendo({
                  ruta: `/api/v1/cxp/${p.ref_id}/documentos/${d.id}/contenido`,
                  nombre: d.nombre || d.nombre_original,
                })
              }
            >
              <Eye size={16} aria-hidden={true} />
            </Boton>
          </p>
        ))}
      </section>
      <VistaPreviaArchivo archivo={viendo} onCerrar={() => setViendo(null)} />
    </div>
  )
}

/**
 * El detalle de una CxC o CxP del Resumen Macro al hacer clic en ella (30/10, pedido del usuario),
 * en una ficha como la de las órdenes. **Sólo se consulta**: cobrar, abonar y generar la orden
 * siguen en sus módulos. Una orden de pago abre su propia ficha, `FichaOrden`.
 */
export function FichaPartida({
  partida,
  tipo,
  onCerrar,
}: {
  partida: Partida
  tipo: "cxc" | "cxp"
  onCerrar: () => void
}) {
  const esOrden = partida.clase === "ORDEN_PAGO"
  const [orden, setOrden] = useState<number | null>(esOrden ? partida.ref_id : null)
  if (orden !== null) return <FichaOrden ordenId={orden} onCerrar={onCerrar} />
  return (
    <Cajon abierto titulo={partida.titulo} onCerrar={onCerrar} ancho="amplio">
      <div className="space-y-4">
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          {[
            CLASE[partida.clase],
            partida.expediente ? `Expediente ${partida.expediente}` : null,
            partida.detalle,
            partida.fecha ? `Vence ${comoFecha(partida.fecha)}` : null,
            partida.nota,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
        {tipo === "cxc" ? (
          <DetalleCxc p={partida} />
        ) : (
          <DetalleCxp p={partida} onOrden={setOrden} />
        )}
      </div>
    </Cajon>
  )
}
